import { useState } from "react";
import { moveDocument, splitTransaction, mergeTransactions } from "../api/client";
import type { StationTransaction } from "../api/client";
import styles from "./RemittanceStation.module.css";

/** SP3 §6 — select-driven repair (no drag-drop). All gated on remittance.balance
 *  by the caller (TransactionPanel renders this only when canBalance). */
export function RepairControls({
  transaction, siblings, onRepaired,
}: {
  transaction: StationTransaction;
  siblings: Array<{ id: string; label: string; ordinal: number }>;
  onRepaired: () => void;
}) {
  const [moveDoc, setMoveDoc] = useState("");
  const [moveTarget, setMoveTarget] = useState("");
  const [splitDoc, setSplitDoc] = useState("");
  const [busy, setBusy] = useState(false);

  const run = async (fn: () => Promise<unknown>) => {
    setBusy(true);
    try { await fn(); onRepaired(); } finally { setBusy(false); }
  };

  const adjacent = siblings.filter((s) => Math.abs(s.ordinal - transaction.ordinal) === 1);

  return (
    <div className={styles.repair}>
      <div className={styles.repairRow}>
        <label>Move
          <select value={moveDoc} onChange={(e) => setMoveDoc(e.target.value)} disabled={busy} aria-label="Document to move">
            <option value="">Document…</option>
            {transaction.documents.map((d) => <option key={d.id} value={d.id}>{d.displayId ?? d.filename}</option>)}
          </select>
          <select value={moveTarget} onChange={(e) => setMoveTarget(e.target.value)} disabled={busy} aria-label="Move target">
            <option value="new">New transaction</option>
            {siblings.map((s) => <option key={s.id} value={s.id}>Transaction {s.ordinal}</option>)}
          </select>
          <button type="button" disabled={busy || !moveDoc} onClick={() => run(() => moveDocument(transaction.id, { documentId: moveDoc, targetTransactionId: moveTarget || "new" }))}>Move</button>
        </label>
      </div>
      <div className={styles.repairRow}>
        <label>Split at
          <select value={splitDoc} onChange={(e) => setSplitDoc(e.target.value)} disabled={busy} aria-label="Split at document">
            <option value="">Document…</option>
            {transaction.documents.map((d) => <option key={d.id} value={d.id}>{d.displayId ?? d.filename}</option>)}
          </select>
          <button type="button" disabled={busy || !splitDoc} onClick={() => run(() => splitTransaction(transaction.id, { documentId: splitDoc }))}>Split</button>
        </label>
      </div>
      <div className={styles.repairRow}>
        <span>Merge adjacent:</span>
        {adjacent.map((s) => (
          <button key={s.id} type="button" disabled={busy} onClick={() => run(() => mergeTransactions(transaction.id, { targetTransactionId: s.id }))}>
            + Transaction {s.ordinal}
          </button>
        ))}
        {adjacent.length === 0 && <span className={styles.muted}>none</span>}
      </div>
    </div>
  );
}
